import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LayoutService } from './layout.service';
import { ThemeService } from './theme.service';
import { CustomPreset } from './theme.types';

/**
 * One step in the theme slideshow — a built-in template or a saved custom
 * preset, optionally paired with a custom layout id. The shape mirrors what
 * the Theme Options dialog already builds, so the dialog can hand its
 * enriched list straight to {@link SlideshowService} without remapping.
 */
export interface SlideItem {
  /** Either a manifest theme id (built-in) or a custom preset id. */
  id: string;
  name: string;
  /** True when `id` refers to one of the user's saved custom presets. */
  isCustom: boolean;
  /** Three sample colors used by the dialog's stage card and (optionally) by
   *  the toolbar tile. Pass through unchanged. */
  swatches: string[];
  type: string;
  /**
   * Optional custom layout to apply alongside the theme. The dialog assigns
   * each slide a layout from its manifest (cycling by index modulo the
   * layout count) so the slideshow showcases both theme + layout variants
   * simultaneously. `null` means "leave the layout alone"; absent means
   * the same.
   */
  layoutId?: string | null;
}

export interface SlideshowState {
  /** True from the moment the user hits Play until they Apply / Save / Cancel.
   *  Drives the toolbar control visibility — when false, no slideshow chrome
   *  shows at all. */
  active: boolean;
  /** Sub-state of `active`: true while the autoplay timer is ticking, false
   *  when paused. Manual prev/next still work while paused. */
  playing: boolean;
  /** Pointer into `items` — which slide is on stage. */
  index: number;
  /** Snapshot of the items list. Cached so consumers (toolbar) don't need to
   *  thread it through separately. */
  items: SlideItem[];
  /** Autoplay cycle length in ms. */
  intervalMs: number;
  /** Convenience: items[index] || null. */
  current: SlideItem | null;
}

const INITIAL_STATE: SlideshowState = {
  active: false,
  playing: false,
  index: 0,
  items: [],
  intervalMs: 3000,
  current: null,
};

/**
 * Drives the "play through every theme" feature outside of the Theme Options
 * dialog. Holds the slideshow state in a singleton so the dialog can close
 * mid-cycle and the dashboard toolbar can keep playing — exposing controls
 * (play / pause / prev / next / apply / save / stop) and a 0–1 `progress$`
 * that the top-nav progress bar binds to.
 *
 * Apply / Save / Cancel semantics mirror the original in-dialog flow:
 *   - applyAndStop  → keep current slide as a pending-preview theme; toolbar
 *                     Save Theme button takes it from there.
 *   - saveAndStop   → commit to localStorage immediately.
 *   - cancelAndStop → revert to whichever theme was active when Play was
 *                     first hit.
 */
@Injectable({ providedIn: 'root' })
export class SlideshowService {
  private themeService = inject(ThemeService);
  private layoutService = inject(LayoutService);
  private zone = inject(NgZone);

  private stateSubject = new BehaviorSubject<SlideshowState>(INITIAL_STATE);
  state$ = this.stateSubject.asObservable();

  /** 0–1 progress through the *current* slide cycle. Resets to 0 on every
   *  slide change; ramps to 1 over `state.intervalMs`. Held at 0 when paused
   *  and at 0 when inactive. */
  private progressSubject = new BehaviorSubject<number>(0);
  progress$ = this.progressSubject.asObservable();

  /** setInterval id for the slide-advance timer. */
  private slideTimer: ReturnType<typeof setInterval> | null = null;
  /** rAF handle for the smooth progress-bar animation. */
  private progressRaf: number | null = null;
  /** performance.now() of the moment the current slide was applied. Used to
   *  compute `(now - cycleStart) / intervalMs`. */
  private cycleStart = 0;

  /**
   * Theme that was active when Play was first hit — captured so a Cancel
   * (or dialog-X close after the user explicitly cancelled) can rewind. The
   * id is null when nothing has been captured yet; once captured, it sticks
   * across pause/play cycles until the slideshow ends.
   */
  private originalId: string | null = null;
  private originalIsCustom = false;
  /** Layout active when Play was first hit — restored on Cancel for the
   *  same reason as `originalId`. Captured even if the slides don't carry
   *  a `layoutId`; the slideshow may rotate layouts and we still need to
   *  revert. */
  private originalLayoutId: string | null = null;
  /** True once `applyCurrentSlide` has fired at least once — i.e. the live
   *  theme has been mutated. Drives whether cancelAndStop needs to revert. */
  private dirty = false;

  // ── Public state accessors (sync) ────────────────────────────────

  get state(): SlideshowState {
    return this.stateSubject.value;
  }

  get isActive(): boolean {
    return this.state.active;
  }

  // ── Lifecycle ────────────────────────────────────────────────────

  /**
   * Begin the slideshow. Captures the currently-active theme so a later
   * Cancel can rewind, applies the slide at `startIndex`, and starts the
   * autoplay timer if `play` is true.
   *
   * Re-calling start() while a slideshow is already running replaces the
   * items list (e.g. the user added a new custom preset) and resets the
   * cursor — but keeps the captured original so a Cancel still rewinds to
   * pre-slideshow state, not pre-restart state.
   */
  start(
    items: SlideItem[],
    startIndex = 0,
    intervalMs = this.state.intervalMs,
    play = true,
  ): void {
    if (!items.length) return;
    this.captureOriginalIfNeeded();
    const safeIndex = Math.max(0, Math.min(startIndex, items.length - 1));
    this.stateSubject.next({
      active: true,
      playing: play,
      index: safeIndex,
      items,
      intervalMs,
      current: items[safeIndex] || null,
    });
    this.applyCurrentSlide();
    if (play) this.startTimers();
  }

  play(): void {
    if (!this.state.active || this.state.items.length === 0) return;
    if (this.state.playing) return;
    this.patch({ playing: true });
    this.startTimers();
  }

  pause(): void {
    if (!this.state.playing) return;
    this.patch({ playing: false });
    this.stopTimers();
  }

  next(): void {
    this.advance(1);
  }

  prev(): void {
    this.advance(-1);
  }

  /** Speed dropdown change. If currently playing, re-arm the timer with the
   *  new interval so the next advance respects it; if paused, just stash. */
  setIntervalMs(intervalMs: number): void {
    if (intervalMs <= 0 || !Number.isFinite(intervalMs)) return;
    this.patch({ intervalMs });
    if (this.state.playing) this.startTimers();
  }

  /**
   * Keep the current slide live as a pending preview (toolbar Save Theme
   * button handles the actual commit) and tear down the slideshow.
   * No revert — the user's intent was to *adopt* this theme.
   */
  applyAndStop(): void {
    const slide = this.state.current;
    if (!slide) {
      this.stop(true);
      return;
    }
    // Re-apply via the preview path so the pending flag is set even if the
    // user paused on a slide before clicking Apply (otherwise the timer-
    // applied preview's pending flag may have been cleared elsewhere).
    this.applySlide(slide);
    this.stop(true);
  }

  /** Commit current slide to localStorage as the saved theme + tear down.
   *  Also commits the pending layout (if any) so the layout the user saw
   *  alongside the chosen theme persists across reloads — otherwise saving
   *  the theme but losing its paired layout would feel like a half-save. */
  saveAndStop(): void {
    const slide = this.state.current;
    if (!slide) {
      this.stop(true);
      return;
    }
    if (slide.isCustom) {
      const preset = this.findCustomPreset(slide.id);
      if (preset) {
        this.themeService.applyCustomPresetPreview(preset);
        this.themeService.commitPendingPreview();
      }
    } else {
      // Built-in template path: drop any custom layer for the same reason
      // as the dialog's saveTemplate (so a pre-slideshow Custom 1 doesn't
      // continue to tint the chosen built-in theme after Save).
      this.themeService.clearCustomOverrides();
      this.themeService.saveTheme(slide.id);
    }
    // Persist the slideshow's current layout pick. commitPendingLayout is a
    // no-op if no layout pending state is active, so this is safe even when
    // the slides didn't carry layout ids.
    this.layoutService.commitPendingLayout();
    this.stop(true);
  }

  /**
   * Tear down without committing — revert to the theme that was active when
   * Play was first hit. No-op if nothing was previewed (dirty=false).
   */
  cancelAndStop(): void {
    if (this.dirty) this.restoreOriginal();
    this.stop(true);
  }

  // ── Internals ────────────────────────────────────────────────────

  private advance(direction: number): void {
    const { items, index, active, playing } = this.state;
    if (!active || items.length === 0) return;
    const next = (index + direction + items.length) % items.length;
    this.patch({ index: next, current: items[next] || null });
    this.applyCurrentSlide();
    // Manual nav (or auto-advance) restarts the cycle clock so the progress
    // bar animates from 0 again. If autoplay is on, also re-arm the timer
    // so a manual jump doesn't trigger a near-immediate auto-advance.
    if (playing) this.startTimers();
    else this.cycleStart = performance.now();
  }

  private applyCurrentSlide(): void {
    const slide = this.state.current;
    if (!slide) return;
    this.dirty = true;
    this.applySlide(slide);
    this.cycleStart = performance.now();
    this.progressSubject.next(0);
  }

  private applySlide(slide: SlideItem): void {
    if (slide.isCustom) {
      const preset = this.findCustomPreset(slide.id);
      if (preset) this.themeService.applyCustomPresetPreview(preset);
    } else {
      // applyTemplatePreview sets the pending flag so the toolbar Save Theme
      // button shows even if the user closes the slideshow without applying —
      // they can still commit the theme they were looking at via that button.
      this.themeService.applyTemplatePreview(slide.id);
    }
    // Layout side: only touch the LayoutService when the slide explicitly
    // names one. `undefined` means "leave the layout alone" (e.g. caller
    // didn't pair layouts with slides), `null` is "explicitly clear", any
    // string is a layout id from the manifest.
    if (slide.layoutId !== undefined) {
      this.layoutService.applyLayoutPreview(slide.layoutId);
    }
  }

  private captureOriginalIfNeeded(): void {
    if (this.originalId !== null) return;
    const orig =
      this.themeService.activePresetId || this.themeService.currentThemeName;
    this.originalId = orig;
    this.originalIsCustom = !!this.findCustomPreset(orig);
    // Snapshot the active layout id too — even if it's null (no layout
    // applied). The cancel path needs to know to call applyLayoutPreview(null)
    // so a slideshow-applied layout is removed on revert, not left behind.
    this.originalLayoutId = this.layoutService.getActiveLayoutId();
  }

  private restoreOriginal(): void {
    const orig = this.originalId;
    if (orig) {
      if (this.originalIsCustom) {
        const preset = this.findCustomPreset(orig);
        if (preset) this.themeService.applyCustomPresetPreview(preset);
      } else {
        // previewTheme (no pending flag) so cancel doesn't leave a Save
        // prompt hanging — the user explicitly opted out.
        this.themeService.previewTheme(orig);
      }
    }
    // Restore layout. applyLayoutPreview re-derives the pending flag from
    // (id !== savedLayoutId), so reverting to the original id automatically
    // clears the pending flag if the original matches what's persisted.
    this.layoutService.applyLayoutPreview(this.originalLayoutId);
    this.dirty = false;
  }

  private findCustomPreset(id: string | null): CustomPreset | null {
    if (!id) return null;
    return this.themeService.getCustomPresets().find((p) => p.id === id) || null;
  }

  /**
   * End the slideshow and clear all local state. `resetCapture` controls
   * whether the captured original-theme bookkeeping is wiped — true after
   * Apply/Save/Cancel (a fresh slideshow gets a fresh original).
   */
  private stop(resetCapture: boolean): void {
    this.stopTimers();
    this.stateSubject.next({ ...INITIAL_STATE, intervalMs: this.state.intervalMs });
    this.progressSubject.next(0);
    if (resetCapture) {
      this.originalId = null;
      this.originalIsCustom = false;
      this.originalLayoutId = null;
      this.dirty = false;
    }
  }

  private startTimers(): void {
    this.stopTimers();
    this.cycleStart = performance.now();
    this.progressSubject.next(0);
    // Run the timers outside Angular's zone so the progress rAF doesn't kick
    // off change detection on every frame — only the BehaviorSubject .next()
    // path needs CD, and that runs through Angular's async pipe naturally.
    this.zone.runOutsideAngular(() => {
      this.slideTimer = setInterval(() => {
        // Re-enter the zone for the state mutation so async pipes update.
        this.zone.run(() => this.advance(1));
      }, this.state.intervalMs);
      this.tickProgress();
    });
  }

  private stopTimers(): void {
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
      this.slideTimer = null;
    }
    if (this.progressRaf !== null) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
  }

  private tickProgress = (): void => {
    if (!this.state.playing || this.state.intervalMs <= 0) {
      this.progressRaf = null;
      return;
    }
    const elapsed = performance.now() - this.cycleStart;
    const ratio = Math.max(0, Math.min(1, elapsed / this.state.intervalMs));
    // Emit on every frame — async pipe in the toolbar template absorbs the
    // change. Coalesces fine because BehaviorSubject de-emits identical refs.
    this.zone.run(() => this.progressSubject.next(ratio));
    this.progressRaf = requestAnimationFrame(this.tickProgress);
  };

  private patch(partial: Partial<SlideshowState>): void {
    this.stateSubject.next({ ...this.state, ...partial });
  }
}
